// ============================================================
// ClipsPlugin — clip creation integration
// Opens /clip/creating?src=<streamKey> when user clicks clip button
// ============================================================
import type { IPlugin, PluginContext } from '../../core/types';
import { ClipsButton } from './ClipsButton';

export class ClipsPlugin implements IPlugin {
  readonly name = 'clips';

  private ctx: PluginContext | null = null;
  private unsubs: Array<() => void> = [];
  private hasUser = false;
  private enabled = false;
  private hidden = false;
  private watchTogether = false;
  private externallyDisabled = false;

  init(ctx: PluginContext): void {
    this.ctx = ctx;

    if (ctx.config.apiData) {
      this.hidden = !!ctx.config.apiData.hidden;
      this.watchTogether = !!ctx.config.apiData.watchTogether;
      this.hasUser = !!ctx.config.apiData.user?.user_id;
      this.syncAvailability();
    }

    this.unsubs.push(
      ctx.eventBus.on('api:data', (data) => {
        this.hidden = !!data.hidden;
        this.watchTogether = !!data.watchTogether;
        this.hasUser = !!data.user?.user_id;
        this.syncAvailability();
      }),
      ctx.eventBus.on('api:user', (user) => {
        this.hasUser = !!user.user_id;
        this.syncAvailability();
      }),
      ctx.eventBus.on('clips:available', () => {
        this.externallyDisabled = false;
        this.syncAvailability();
      }),
      ctx.eventBus.on('clips:disabled', () => {
        this.externallyDisabled = true;
        this.syncAvailability();
      }),
      ctx.eventBus.on('clips:create', () => {
        this.createClip();
      }),
    );

    ctx.uiRegistry.register('clips-button', ClipsButton);

    ctx.logger.debug('ClipsPlugin initialized');
  }

  private syncAvailability(): void {
    this.enabled = this.hasUser && !this.hidden && !this.watchTogether && !this.externallyDisabled;
    this.ctx?.store.setState({ clipsAvailable: this.enabled });
  }

  /** Check if clip creation is available (user is logged in, stream not hidden) */
  get isAvailable(): boolean {
    return this.enabled;
  }

  /** Open clip creation page in a new tab */
  createClip(): void {
    if (!this.enabled) return;
    const streamKey = this.ctx?.store.getState().streamKey;
    if (streamKey) {
      window.open(`/clip/creating?src=${encodeURIComponent(streamKey)}`);
    }
  }

  destroy(): void {
    this.ctx?.uiRegistry.unregister('clips-button', ClipsButton);
    this.ctx?.store.setState({ clipsAvailable: false });
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    this.ctx = null;
    this.hasUser = false;
    this.enabled = false;
    this.hidden = false;
    this.watchTogether = false;
    this.externallyDisabled = false;
  }
}
